import { AfterViewInit, Component, input, ViewEncapsulation, inject } from '@angular/core';
import { NgControl, FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';

import { ApContentService } from '@ap/content';
import { AbstractInputComponent, ApAutocompleteFilterService, ApAutocompleteModule, InputmodeType } from '@ap/input';
import { ApInputConstraintService } from '@ap/input/constraint';
import { ApEmailValidator, maxlengthEmail } from '@ap/input/validators';
import { ApIconComponent } from '@ap/icon';
import { ApFormErrorsComponent } from '@ap/form/errors';
import { ApSuggestionComponent } from '@ap/input/suggestion';

import { ApEmailSuggestionService } from './suggestion/email-suggestion.service';
import content from './email-input.content.json';

@Component({
    selector: 'ap-email-input',
    templateUrl: '../../../src/lib/input.component.html',
    styleUrls: ['../../../src/lib/input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, ApIconComponent, ApSuggestionComponent, ApFormErrorsComponent, ApAutocompleteModule, NgTemplateOutlet],
    providers: [ApInputConstraintService, ApEmailSuggestionService]
})
export class ApEmailInputComponent extends AbstractInputComponent implements AfterViewInit {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);
    private readonly emailSuggestionService = inject(ApEmailSuggestionService);
    protected readonly filterService = inject(ApAutocompleteFilterService);

    /**
     * Maxlength of an email address
     */
    protected readonly maxlength = input<number>(maxlengthEmail);

    /**
     * Characters that are stripped
     */
    protected readonly stripPattern = input<RegExp>(/[^\w@!#$%&'*+-/=?^_`{|}~]/i);

    /**
     * Inputmode (Standard HTML inputmode types)
     */
    protected readonly inputmode = input<InputmodeType>('email');

    constructor() {
        super();
        this.setDefaultContent(content, 'email-input');
    }

    public ngAfterViewInit(): void {
        this.setValidation();
    }

    /**
     * Check suggestion service if the current value matches a suggestion
     */
    protected override onBlur(): void {
        super.onBlur();
        if (this.control?.valid) {
            const { value } = this;
            const suggestion = this.emailSuggestionService.run(value);
            if (suggestion && value !== suggestion && this.suggestionValue !== suggestion) {
                this.showSuggestion(suggestion);
            }
        }
    }

    private setValidation() {
        const control = this.control?.control;
        if (control) {
            control.addValidators([ApEmailValidator.validate]);
            control.updateValueAndValidity();
        }
    }
}
